/**
 * H/L Jump — a daily momentum oscillator: the summed distance of close from its
 * 17/50/200 SMAs, normalised against that sum's own 200-bar extremes to a ±100
 * scale. Extracted from `calcHighJump` in `lib/indicators/customIndexEtf.ts` so
 * the chart `MDA_HIGH_JUMP` study and this shared def compute identically.
 *
 *   ttldiff = (close-SMA17) + (close-SMA50) + (close-SMA200)
 *   HJ = ttldiff>0 ?  ttldiff / highest(ttldiff,200) * 100
 *      : ttldiff<0 ?  ttldiff / lowest(ttldiff,200)  * -100
 *      : 0
 */
import { familyGroup } from "../indicatorGroups";
import type { OHLCV, IndicatorParams, SharedParam, SharedIndicatorDef } from "../types";
import { smaFull, highestAt, lowestAt } from "./ta";

const len = (v: number | undefined, d: number) =>
  Math.max(1, Math.round(typeof v === "number" && Number.isFinite(v) ? v : d));

/** Configurable H/L Jump inputs — the three SMA lengths summed into `ttldiff`
 *  and the lookback the result is normalized against. Defaults 17/50/200/200
 *  reproduce the canonical H/L Jump (and the chart `MDA_HIGH_JUMP` study). */
export const HL_JUMP_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "sma1", label: "SMA 1", kind: "number", default: 17, min: 1, max: 400, step: 1 },
  { id: "sma2", label: "SMA 2", kind: "number", default: 50, min: 1, max: 400, step: 1 },
  { id: "sma3", label: "SMA 3", kind: "number", default: 200, min: 1, max: 400, step: 1 },
  { id: "normWindow", label: "Normalize window", kind: "number", default: 200, min: 10, max: 1000, step: 10 },
];

export function computeHighJump(data: OHLCV, p: IndicatorParams = {}): (number | null)[] {
  const { closes } = data;
  const n = closes.length;
  const out: (number | null)[] = new Array(n).fill(null);
  if (n === 0) return out;
  const ma17 = smaFull(closes, len(p.sma1, 17));
  const ma50 = smaFull(closes, len(p.sma2, 50));
  const ma200 = smaFull(closes, len(p.sma3, 200));
  const norm = len(p.normWindow, 200);
  const ttldiff = closes.map(
    (c, i) => c - ma17[i]! + (c - ma50[i]!) + (c - ma200[i]!),
  );
  for (let i = 0; i < n; i++) {
    const d = ttldiff[i]!;
    if (d > 0) {
      const hi = highestAt(ttldiff, norm, i);
      out[i] = hi > 0 ? (d / hi) * 100 : 0;
    } else if (d < 0) {
      const lo = lowestAt(ttldiff, norm, i);
      out[i] = lo < 0 ? (d / lo) * -100 : 0;
    } else {
      out[i] = 0;
    }
  }
  return out;
}

export const HL_JUMP: SharedIndicatorDef = {
  id: "mpp-hl-jump",
  label: "Afiat - Stretch",
  group: familyGroup("hljump"),
  format: "signed",
  signColored: true,
  referenceLevels: [95, -95],
  // Normalized against a 200-bar extreme → too jumpy per-trade; the scanner's
  // live overlay skips it so it tracks the chart on the ~60s batch cadence.
  noLiveOverlay: true,
  params: HL_JUMP_PARAMS,
  computeSeries: (data, p) => computeHighJump(data, p),
};
